import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import type { FamilyPortalUser } from '../../../dtos/portal/familyPortalAdmin.dto';

const MIN_REASON = 5;

interface Age18ReviewDialogProps {
  open: boolean;
  portalUser: FamilyPortalUser | null;
  busy: boolean;
  onConfirm: (decision: 'continue' | 'revoke', reason: string) => void;
  onCancel: () => void;
}

/** The patient turned 18: record whether this guardian's authority continues, or revoke (Q7). */
const Age18ReviewDialog: React.FC<Age18ReviewDialogProps> = ({ open, portalUser, busy, onConfirm, onCancel }) => {
  const id = useId();
  const [decision, setDecision] = useState<'continue' | 'revoke' | ''>('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setDecision('');
      setReason('');
    }
  }, [open]);

  if (!portalUser) return null;
  const valid = decision !== '' && reason.trim().length >= MIN_REASON;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (valid && !busy && decision) onConfirm(decision, reason.trim());
  };

  return (
    <Dialog open={open} onClose={busy ? () => undefined : onCancel} labelledBy={`${id}-title`}>
      <form onSubmit={submit} className="text-left">
        <h3 id={`${id}-title`} className="pr-6 text-lg font-semibold text-slate-800">
          Review guardian access at 18
        </h3>
        <p className="mt-1 text-sm text-slate-700">
          The patient is now an adult. Confirm whether {portalUser.firstName} {portalUser.lastName} still has legal authority
          (for example, adult guardianship), or end their access.
        </p>
        <fieldset className="mt-4">
          <legend className="text-sm font-medium text-slate-700">
            Decision <span className="text-red-600" aria-hidden="true">*</span>
          </legend>
          <label className="mt-2 flex items-center gap-2 text-sm text-slate-800">
            <input
              type="radio"
              name={`${id}-decision`}
              value="continue"
              checked={decision === 'continue'}
              onChange={() => setDecision('continue')}
              disabled={busy}
            />
            Authority continues: keep access
          </label>
          <label className="mt-1 flex items-center gap-2 text-sm text-slate-800">
            <input
              type="radio"
              name={`${id}-decision`}
              value="revoke"
              checked={decision === 'revoke'}
              onChange={() => setDecision('revoke')}
              disabled={busy}
            />
            End access (revoke)
          </label>
        </fieldset>
        <label htmlFor={`${id}-reason`} className="mt-4 block text-sm font-medium text-slate-700">
          Reason <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <textarea
          id={`${id}-reason`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={1000}
          required
          aria-required="true"
          aria-describedby={`${id}-hint`}
          disabled={busy}
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
        />
        <p id={`${id}-hint`} className="mt-1 text-xs text-slate-600">
          At least {MIN_REASON} characters. Saved with the record history.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            className={`rounded-md px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50 ${
              decision === 'revoke' ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-800 hover:bg-slate-700'
            }`}
          >
            {busy ? 'Saving...' : 'Save review'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default Age18ReviewDialog;
